import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import { Icon } from 'components/icon/icon';
import { Button } from 'components/button';
import { NextLink } from 'components/next-link';
import { Link } from 'components/link/link';
import { useOutsideClick } from 'utils/use-outside-click';

import IcnCarrotDown from 'public/assets/icons/carot-down.svg';
import styles from './dropdown.module.scss';
import { toKebabCase } from 'utils/kebab-case';

export interface IDropdownProps {
	label: string;
	onClick: () => void;
	linkGroups: {
		title: string;
		links: { title: string; href: string }[];
		href?: string;
	}[];
}

const Dropdown = (props: IDropdownProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
	const intl = useIntl();
	const router = useRouter();
	const buttonRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const closeDropdown = useCallback(() => {
		setIsOpen(false);
		setMenuPosition(null);
	}, []);

	const updateMenuPosition = useCallback(() => {
		if (buttonRef.current && typeof window !== 'undefined') {
			const rect = buttonRef.current.getBoundingClientRect();
			setMenuPosition({
				top: rect.bottom,
				left: 0,
				width: window.innerWidth
			});
		}
	}, []);

	const handleMouseEnter = useCallback(() => {
		if (!isOpen) {
			updateMenuPosition();
		}
		setIsOpen(true);
	}, [isOpen, updateMenuPosition]);

	const handleMouseLeave = useCallback(() => {
		closeDropdown();
	}, [closeDropdown]);

	const handleClick = useCallback(() => {
		if (!isOpen) {
			updateMenuPosition();
		}
		setIsOpen((prev) => !prev);
	}, [isOpen, updateMenuPosition]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape' && isOpen) {
				closeDropdown();
				buttonRef.current?.focus();
			}
		},
		[isOpen, closeDropdown]
	);

	const onRouteChangeStart = useCallback(() => {
		closeDropdown();
	}, [closeDropdown]);

	// Handle outside clicks
	const handleOutsideClick = useCallback(() => {
		if (isOpen) {
			closeDropdown();
		}
	}, [isOpen, closeDropdown]);

	useOutsideClick([buttonRef, menuRef], handleOutsideClick);

	// Handle keyboard events
	useEffect(() => {
		if (isOpen) {
			document.addEventListener('keydown', handleKeyDown);
			return () => {
				document.removeEventListener('keydown', handleKeyDown);
			};
		}
	}, [isOpen, handleKeyDown]);

	// Handle route changes
	useEffect(() => {
		router.events.on('routeChangeStart', onRouteChangeStart);
		return () => {
			router.events.off('routeChangeStart', onRouteChangeStart);
		};
	}, [router.events, onRouteChangeStart]);

	// Lock body scroll when dropdown is open
	useEffect(() => {
		if (typeof document === 'undefined') return;

		if (isOpen) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			const originalOverflow = document.body.style.overflow;
			const originalPaddingRight = document.body.style.paddingRight;

			document.body.style.overflow = 'hidden';
			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			return () => {
				document.body.style.overflow = originalOverflow;
				document.body.style.paddingRight = originalPaddingRight;
			};
		}
	}, [isOpen]);

	// Update position on scroll/resize when open
	useEffect(() => {
		if (isOpen) {
			const handleResize = (): void => {
				updateMenuPosition();
			};

			window.addEventListener('scroll', updateMenuPosition, true);
			window.addEventListener('resize', handleResize);

			return () => {
				window.removeEventListener('scroll', updateMenuPosition, true);
				window.removeEventListener('resize', handleResize);
			};
		}
	}, [isOpen, updateMenuPosition]);

	// Close on route change
	useEffect(() => {
		closeDropdown();
	}, [router.asPath, closeDropdown]);

	const renderMenu = (): React.JSX.Element | null => {
		if (!isOpen || !menuPosition) return null;

		const maxHeight = typeof window === 'undefined' ? '80vh' : `${window.innerHeight - menuPosition.top - 24}px`;

		const menuContent = (
			<div
				ref={menuRef}
				className={clsx(styles.collapsibleMenu, styles.isOpen)}
				style={{
					position: 'fixed',
					top: `${menuPosition.top}px`,
					left: `${menuPosition.left}px`,
					width: `${menuPosition.width}px`,
					maxHeight
				}}
				role="menu"
				aria-label={
					isOpen
						? intl.formatMessage({ id: 'subsidy.status.open' })
						: intl.formatMessage({ id: 'subsidy.status.closed' })
				}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<div className={styles.content}>
					{props.linkGroups.map((item, index) => {
						const id = toKebabCase(item.title);

						return (
							<nav aria-labelledby={id} className={clsx(styles.contentGroup, item?.href && styles.hasLink)} key={index}>
								<h2>
									{item?.href ? (
										<NextLink id={id} href={item.href}>
											{item?.title}
										</NextLink>
									) : (
										<a href="#" id={id}>
											{item?.title}
										</a>
									)}
								</h2>
								{item?.links?.map((link, idx) => (
									<div className={styles.link} key={idx}>
										<Link variant="tertiary-dark" href={link?.href}>
											{link?.title}
										</Link>
									</div>
								))}
							</nav>
						);
					})}
				</div>
			</div>
		);

		// Render via portal if available, otherwise render inline
		if (typeof document !== 'undefined') {
			return ReactDOM.createPortal(menuContent, document.body);
		}

		return menuContent;
	};

	return (
		<div className={styles.Dropdown} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
			<div ref={buttonRef} className={styles.button} role="button" aria-haspopup="true" aria-expanded={isOpen}>
				<Button
					variant="tertiary-dark"
					title={props.label}
					icon={<Icon customIcon={<IcnCarrotDown />} size="14px" />}
					iconPosition="right"
					onClick={handleClick}
					ariaExpanded={isOpen}
				/>
			</div>
			{renderMenu()}
		</div>
	);
};

export { Dropdown };
